import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFinanceTransactions } from "../api/finance-api";
import { useEventHistory } from "../api/events-api";
import { useLogeMembers } from "../api/user-api";
import { AnimatedKr } from "../components/dss/finance/AnimatedKr";
import { BalanceChart } from "../components/dss/finance/BalanceChart";
import { TransactionRow } from "../components/dss/finance/TransactionRow";
import { VaultSkeleton } from "../components/dss/finance/VaultSkeleton";
import { Icon } from "../components/dss/Icon";
import { FadeIn, useEnterAnimation } from "../components/dss/Motion";
import { SegmentedTabs } from "../components/dss/SegmentedTabs";
import {
  buildMonthlySeries,
  currentBalanceOre,
  formatKr,
  newestFirst,
  Range,
  RANGE_LABELS,
  sliceRange,
} from "../finance/ledger";
import { useSupabaseAuth } from "../supabase/SupabaseContext";
import { FinanceTransaction } from "../types/entities";

/** How many movements the chart screen shows before handing over to the
 *  ledger. The list is the supporting act here; a wall of rows under the
 *  chart would make it the main one. */
const COLLAPSED_ROWS = 6;

const RANGES: Range[] = ["ytd", "1y", "5y", "all"];

/** What the change under the balance is measured over, said in Danish — the
 *  control's own labels are abbreviations, and "+4.879,78 1Y" reads like a
 *  stock ticker rather than a sentence. */
const RANGE_CAPTIONS: Record<Range, string> = {
  ytd: "i år",
  "1y": "12 mdr.",
  "5y": "5 år",
  all: "i alt",
};

/** "september 26" → "sep 26", for the one place both ends have to fit on a line. */
const shortMonth = (label: string) => {
  const [month, year] = label.split(" ");
  return `${month.slice(0, 3)} ${year}`;
};

const FinancePage: FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useSupabaseAuth();
  const { data: transactions = [], isLoading } = useFinanceTransactions();
  // Only to put a name on a booking; the ledger stands on its own without
  // either, so neither is awaited before drawing. The *history* hook, not the
  // calendar one: an arrangement is booked for an evening that has already
  // happened, and the calendar hook cuts everything before today, so it could
  // never once have found the samling a row was pointing at.
  const { data: events = [] } = useEventHistory();
  const { data: members = [] } = useLogeMembers();

  const animateIn = useEnterAnimation(isLoading);
  const isAdmin = hasRole("ADMIN");

  const [range, setRange] = useState<Range>("ytd");
  const [previousRange, setPreviousRange] = useState<Range | undefined>();
  /** The month under the finger, or null when the screen is showing today. */
  const [scrubIndex, setScrubIndex] = useState<number | null>(null);
  const [rangeChanged, setRangeChanged] = useState(false);

  useEffect(() => {
    if (rangeChanged) {
      const timer = setTimeout(() => setRangeChanged(false), 600);
      return () => clearTimeout(timer);
    }
  }, [rangeChanged]);

  const allPoints = useMemo(() => buildMonthlySeries(transactions), [transactions]);
  const points = useMemo(() => sliceRange(allPoints, range), [allPoints, range]);

  const balanceOre = currentBalanceOre(transactions);
  const active = scrubIndex != null ? points[scrubIndex] : null;
  const shownBalanceOre = active ? active.balanceOre : balanceOre;

  // What the range has done to the balance. Measured from the balance the
  // loge *entered* the range with: a point's balance is the one its month
  // closed on, so the opening figure is the first point's, minus its own net.
  const openingOre = points.length ? points[0].balanceOre - points[0].netOre : balanceOre;
  const closingOre = active
    ? active.balanceOre
    : points[points.length - 1]?.balanceOre ?? balanceOre;
  const changeOre = closingOre - openingOre;

  const rangeIncomeOre = points.reduce((sum, p) => sum + p.incomeOre, 0);
  const rangeExpenseOre = points.reduce((sum, p) => sum + p.expenseOre, 0);

  const eventsById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const membersById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);

  // Newest first for reading, which is the opposite of the order the running
  // balance is computed in. Sorted outright rather than by reversing the
  // flattened series: the months run oldest first but the rows inside each
  // one already run newest first, so a reverse turned every month upside down.
  const listed: FinanceTransaction[] = useMemo(
    () => points.flatMap((p) => p.transactions).sort(newestFirst),
    [points]
  );
  const visible = listed.slice(0, COLLAPSED_ROWS);

  const selectRange = (key: string) => {
    setPreviousRange(range);
    setScrubIndex(null);
    setRange(key as Range);
    setRangeChanged(true);
  };

  const empty = !isLoading && points.length === 0;

  return (
    // The horizontal padding lives on the sections rather than here, so the
    // chart can run the full width of the screen without having to claw it
    // back with a negative margin.
    <div
      data-scroll-container
      data-sticky-scroll
      className="dss-vault flex-1 overflow-y-auto text-vault-ink"
    >
      {isAdmin && (
        <div className="px-7 pt-2 flex justify-end">
          <button
            onClick={() => navigate("/finance/add")}
            aria-label="Ny postering"
            className="-m-1.5 p-1.5 text-vault-ink3 flex-shrink-0"
          >
            <Icon name="plus" size={17} />
          </button>
        </div>
      )}
      <div className="px-7 font-display text-[52px] leading-[0.9] tracking-tight font-extrabold mt-1.5 uppercase">
        Finans<span className="text-mint">.</span>
      </div>

      {/* ── The balance, and the curve under it ───────────────────────── */}
      {/* No card. A card gives the curve two edges to sit inside and about
          fifty pixels less room to say anything; edge to edge, the line is
          the screen rather than an illustration on it. The band's own light
          is what separates it from the movements below. */}
      <div className="dss-vault-band pt-5 pb-4 mt-3">
        <div className="px-7">
          <div className="font-mono text-[9.5px] tracking-[1.5px] uppercase text-vault-ink3 h-3">
            {active ? active.label : "Saldo i dag"}
          </div>

          {isLoading ? (
            <VaultSkeleton className="w-52 h-[44px] mt-2" />
          ) : (
            <div className="font-display font-extrabold text-[46px] leading-[1.05] tracking-tight mt-1">
              <AnimatedKr
                amountOre={shownBalanceOre}
                // No easing while a month is under the finger: see AnimatedKr.
                // Letting go springs back to today's balance, which is a real
                // transition and keeps its animation.
                immediate={active != null}
                // Counts up from nothing the first time the screen is opened,
                // and only then — coming back to a tab you've already visited
                // should show the number, not perform it again.
                enterFrom={animateIn ? 0 : undefined}
                prefixClassName="font-mono text-[15px] font-medium text-vault-ink3 mr-0.5"
                decimalsClassName="text-[26px] text-vault-ink2"
              />
            </div>
          )}

          {/* Under the balance: what the range has done to it, or — while a
              month is under the finger — what that month itself did. */}
          <div className="mt-2.5 h-5 flex items-center gap-2.5 font-mono text-[10.5px] tracking-wide">
            {isLoading ? (
              <VaultSkeleton className="w-32 h-3" />
            ) : active ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-mint">
                  <Icon name="arrow-trend-up" size={10} />
                  {formatKr(active.incomeOre, { prefix: false })}
                </span>
                <span className="inline-flex items-center gap-1.5 text-ember">
                  <Icon name="arrow-trend-down" size={10} />
                  {formatKr(active.expenseOre, { prefix: false })}
                </span>
              </>
            ) : (
              <>
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded ${
                    changeOre >= 0 ? "bg-mint/10 text-mint" : "bg-ember/10 text-ember"
                  }`}
                >
                  <Icon
                    name={changeOre >= 0 ? "arrow-trend-up" : "arrow-trend-down"}
                    size={10}
                  />
                  {formatKr(changeOre, { signed: true, prefix: false })}
                </span>
                <span className="text-vault-ink3 uppercase tracking-[1.3px] text-[9.5px]">
                  {RANGE_CAPTIONS[range]}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="mt-4">
          {isLoading ? (
            <VaultSkeleton className="mx-7 h-[190px]" />
          ) : empty ? (
            <div className="mx-7 h-[190px] rounded border border-dashed border-white/10 flex flex-col items-center justify-center gap-2 text-vault-ink3">
              <Icon name="chart-line" size={18} />
              <div className="font-mono text-[9.5px] tracking-[1.4px] uppercase">
                Ingen posteringer endnu
              </div>
            </div>
          ) : (
            <BalanceChart
              // Framer Motion's initial/animate only plays on mount, and this
              // component never unmounts when the range changes — so without
              // a key change, toggling animateIn does nothing. Keying on the
              // range forces a remount, which is what actually replays the
              // draw-in.
              key={range}
              points={points}
              activeIndex={scrubIndex}
              onScrub={setScrubIndex}
              animateIn={animateIn || rangeChanged}
            />
          )}
        </div>

        {!empty && (
          <div className="px-7 mt-3 flex items-center justify-between gap-3">
            {/* Truncated to three letters: "januar 26 – september 26" wraps
                onto a second line beside the range control on a narrow
                phone, and shoves the card's own footer out of alignment. */}
            <div className="font-mono text-[9px] tracking-[1.2px] uppercase text-vault-ink3 truncate">
              {points.length > 0 &&
                `${shortMonth(points[0].label)} – ${shortMonth(points[points.length - 1].label)}`}
            </div>
            <SegmentedTabs
              tone="vault"
              variant="compact"
              segments={RANGES.map((r) => ({ key: r, label: RANGE_LABELS[r] }))}
              activeKey={range}
              enterFrom={previousRange}
              onSelect={selectRange}
            />
          </div>
        )}
      </div>

      {/* ── In and out over the range ─────────────────────────────────── */}
      {!empty && (
        <FadeIn enabled={animateIn} delay={0.12} className="px-7 mt-5 grid grid-cols-2 gap-2.5">
          <FlowCard
            label="Indgået"
            icon="arrow-trend-up"
            amountOre={rangeIncomeOre}
            tone="mint"
            loading={isLoading}
            animateIn={animateIn}
          />
          <FlowCard
            label="Udgået"
            icon="arrow-trend-down"
            amountOre={rangeExpenseOre}
            tone="ember"
            loading={isLoading}
            animateIn={animateIn}
          />
        </FadeIn>
      )}

      {/* ── The movements themselves ──────────────────────────────────── */}
      <div className="px-7 mt-6 pb-5">
        <div className="flex items-baseline justify-between gap-3 mb-2.5">
          <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-vault-ink3">
            [ Bevægelser ]
          </div>
          {transactions.length > 0 && (
            <button
              onClick={() => navigate("/finance/transactions")}
              className="font-mono text-[9.5px] tracking-[1.3px] uppercase text-vault-ink2 inline-flex items-center gap-1.5"
            >
              {`Vis alle ${transactions.length}`}
              <Icon name="chevron-right" size={9} />
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="dss-vault-card rounded-lg overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 px-3.5 py-3">
                <VaultSkeleton className="w-8 h-8 flex-shrink-0" />
                <div className="flex-1 flex flex-col gap-1.5">
                  <VaultSkeleton className="w-2/3 h-3.5" />
                  <VaultSkeleton className="w-1/3 h-2.5" />
                </div>
              </div>
            ))}
          </div>
        ) : listed.length === 0 ? (
          <div className="dss-vault-card rounded-lg px-4 py-6 text-center font-mono text-[10px] tracking-[1.3px] uppercase text-vault-ink3">
            Intet bogført i perioden
          </div>
        ) : (
          <div className="dss-vault-card rounded-lg overflow-hidden divide-y divide-white/[0.06]">
            {visible.map((t) => (
              <TransactionRow
                key={t.id}
                transaction={t}
                event={t.loge_event_id ? eventsById.get(t.loge_event_id) : undefined}
                member={t.loge_member_id ? membersById.get(t.loge_member_id) : undefined}
                onClick={isAdmin ? () => navigate(`/finance/${t.id}`) : undefined}
              />
            ))}
          </div>
        )}

        {isAdmin ? (
          <button
            onClick={() => navigate("/finance/add")}
            className="mt-2.5 w-full py-3.5 rounded-lg dss-vault-card text-vault-ink font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between px-4"
          >
            <span className="inline-flex items-center gap-2">
              <Icon name="plus" size={14} /> Ny postering
            </span>
            <span className="font-mono text-[10px] tracking-wide text-vault-ink3">
              IND ELLER UD
            </span>
          </button>
        ) : (
          <div className="mt-4 pt-3 flex items-center justify-center gap-2 font-mono text-[10px] tracking-wide uppercase text-vault-ink3">
            <Icon name="lock" size={11} />
            <span>Kun admin kan bogføre</span>
          </div>
        )}
      </div>
    </div>
  );
};

function FlowCard({
  label,
  icon,
  amountOre,
  tone,
  loading,
  animateIn,
}: {
  label: string;
  icon: "arrow-trend-up" | "arrow-trend-down";
  amountOre: number;
  tone: "mint" | "ember";
  loading: boolean;
  animateIn: boolean;
}) {
  return (
    <div className="dss-vault-card rounded-lg px-4 py-3.5">
      <div className="flex items-center gap-1.5 font-mono text-[9px] tracking-[1.3px] uppercase text-vault-ink3">
        <Icon name={icon} size={9} className={tone === "mint" ? "text-mint" : "text-ember"} />
        {label}
      </div>
      {loading ? (
        <VaultSkeleton className="w-20 h-5 mt-2" />
      ) : (
        <div
          className={`font-display font-extrabold text-[19px] leading-none tracking-tight mt-2 ${
            tone === "mint" ? "text-mint" : "text-ember"
          }`}
        >
          <AnimatedKr
            amountOre={amountOre}
            enterFrom={animateIn ? 0 : undefined}
            prefix={false}
            decimalsClassName="text-[13px] opacity-60"
          />
        </div>
      )}
    </div>
  );
}

export default FinancePage;
